import React, { useState, useEffect } from 'react';
import { Goal } from '../types';
import {
  CapabilityDomain,
  CapabilityDecision,
  CAPABILITY_DOMAIN_LABELS,
} from './types';
import {
  validateAssessment,
  isDomainDecisionComplete,
  calculateProgress,
} from './capabilityDecision.service';
import { saveAssessment } from './capabilityStorage.service';
import CapabilityDomainStep from './CapabilityDomainStep';
import CapabilitySummary from './CapabilitySummary';

interface CapabilityWizardProps {
  goal: Goal;
  ownerId: string;
  onComplete: () => void | Promise<void>; // ✅ FIX: Allow async callbacks
  onCancel: () => void;
  skipSummary?: boolean; // If true, skip summary and auto-save after last domain
}

const ALL_DOMAINS: CapabilityDomain[] = [
  'strategy',
  'marketing',
  'sales',
  'product',
  'operations',
  'finance',
  'team',
];

/**
 * CapabilityWizard - Main wizard component
 * 
 * Multi-step wizard voor capability assessment.
 * Keuzedwingend, blokkerend bij incomplete beslissingen.
 * Milestones will be generated during sparring with full context.
 */
const CapabilityWizard: React.FC<CapabilityWizardProps> = ({
  goal,
  ownerId,
  onComplete,
  onCancel,
  skipSummary = false,
}) => {
  // Initialize decisions for all domains
  const [decisions, setDecisions] = useState<CapabilityDecision[]>(() =>
    ALL_DOMAINS.map(domain => ({
      domain,
      required: undefined as any, // Will be set by user
    }))
  );

  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentDomain = ALL_DOMAINS[currentDomainIndex];
  const currentDecision = decisions[currentDomainIndex];
  const progress = calculateProgress(decisions, ALL_DOMAINS.length);

  // Update a decision
  const handleUpdateDecision = (updates: Partial<CapabilityDecision>) => {
    setDecisions(prev => {
      const updated = [...prev];
      updated[currentDomainIndex] = {
        ...updated[currentDomainIndex],
        ...updates,
      };
      return updated;
    });
  };

  // Navigate to next domain
  const handleNext = () => {
    if (!isDomainDecisionComplete(currentDecision)) {
      setError('Maak eerst alle keuzes voor dit domein.');
      return;
    }

    setError(null);

    if (currentDomainIndex < ALL_DOMAINS.length - 1) {
      setCurrentDomainIndex(prev => prev + 1);
    } else {
      // All domains done, validate and show summary OR auto-save
      const validation = validateAssessment(decisions);
      if (!validation.isValid) {
        if (validation.blockingDomains.length > 0) {
          setError('Er zijn nog domeinen waar "Nog onbekend" is gekozen. Kies een verantwoordelijkheid.');
        } else {
          setError('Niet alle vereiste keuzes zijn gemaakt.');
        }
        // Go back to first incomplete domain
        const firstIncomplete = decisions.findIndex(d => !isDomainDecisionComplete(d));
        if (firstIncomplete >= 0) {
          setCurrentDomainIndex(firstIncomplete);
        }
        return;
      }

      // If skipSummary is true, auto-save and complete
      if (skipSummary) {
        handleConfirm();
      } else {
        setShowSummary(true);
      }
    }
  };

  // Navigate to previous domain
  const handlePrevious = () => {
    if (currentDomainIndex > 0) {
      setCurrentDomainIndex(prev => prev - 1);
      setError(null);
    }
  };

  // Save assessment only - milestones will be created during/after sparring
  const handleConfirm = async () => {
    console.log('💾 [CapabilityWizard] handleConfirm called');
    setIsCreating(true);
    setError(null);

    try {
      // Save assessment to Firebase (NO milestone creation here!)
      // Milestones will be generated during sparring with full context
      await saveAssessment(ownerId, goal.id, {
        decisions,
        completedAt: Date.now(),
      });
      
      console.log('✅ [CapabilityWizard] Assessment saved, calling onComplete');

      // ✅ CRITICAL FIX: Wait for onComplete to finish!
      // This ensures that AssessmentFlow has time to load the assessment,
      // call completeAssessment(), and set currentStep to 'milestones_review'
      // BEFORE we return control to the parent component.
      await onComplete();
      
      console.log('🎯 [CapabilityWizard] onComplete finished successfully');
      // ✅ FIX: Reset loading state after successful completion
      // Note: Component will unmount when AssessmentFlow changes step,
      // but we reset state here for safety
      setIsCreating(false);
    } catch (err: any) {
      console.error('Failed to save assessment:', err);
      setError(err.message || 'Er ging iets mis bij het opslaan van de assessment.');
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90dvh] overflow-hidden flex flex-col">
        {!showSummary ? (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Capaciteiten checken
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Voor doel: {goal.title}
                  </p>
                </div>
                <button
                  onClick={onCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Domein {currentDomainIndex + 1} van {ALL_DOMAINS.length}</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-900 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto scrollbar-always px-6 py-6">
              <CapabilityDomainStep
                decision={currentDecision}
                onUpdate={handleUpdateDecision}
              />

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentDomainIndex === 0}
                className="
                  px-4 py-2 text-gray-700 hover:text-gray-900
                  disabled:opacity-30 disabled:cursor-not-allowed
                "
              >
                Vorige
              </button>
              <button
                onClick={handleNext}
                disabled={!isDomainDecisionComplete(currentDecision)}
                className="
                  px-6 py-2 bg-gray-900 text-white rounded-lg
                  hover:bg-gray-800 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {currentDomainIndex < ALL_DOMAINS.length - 1 ? 'Volgende' : 'Afronden'}
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto scrollbar-always p-6">
            <CapabilitySummary
              decisions={decisions}
              onConfirm={handleConfirm}
              onCancel={() => setShowSummary(false)}
              isCreating={isCreating}
            />
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CapabilityWizard;
